import {
  CarouselDir,
  UseCarouselFunc,
  UseCarouselOptions,
} from "@model/Aboutus.model";
import { useCallback, useEffect, useRef, useState } from "react";

export function useCarousel(options: UseCarouselOptions = {}): UseCarouselFunc {
  const { step = 0.85, epsilon = 1, observe = true } = options;

  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const getMaxScrollLeft = (el: HTMLDivElement) =>
    Math.max(0, el.scrollWidth - el.clientWidth);

  const computeEdgeState = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    const max = getMaxScrollLeft(el);
    if (max <= epsilon) {
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    const sl = el.scrollLeft;
    setAtStart(sl <= epsilon);
    setAtEnd(sl >= max - epsilon);
  }, [epsilon]);

  const scrollBy = useCallback(
    (dir: CarouselDir) => {
      const el = viewportRef.current;
      if (!el) return;
      el.scrollBy({
        left: el.clientWidth * step * (dir === "next" ? 1 : -1),
        behavior: "smooth",
      });
      requestAnimationFrame(computeEdgeState);
    },
    [computeEdgeState, step]
  );

  const scrollPrev = useCallback(() => scrollBy("prev"), [scrollBy]);
  const scrollNext = useCallback(() => scrollBy("next"), [scrollBy]);

  const reset = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, top: 0, behavior: "auto" });
    requestAnimationFrame(computeEdgeState);
  }, [computeEdgeState]);

  useEffect(() => {
    if (!observe) return;

    const el = viewportRef.current;
    if (!el) return;

    const raf = requestAnimationFrame(computeEdgeState);

    const onScroll = () => computeEdgeState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", computeEdgeState);

    const observers: ResizeObserver[] = [];
    if (typeof ResizeObserver !== "undefined") {
      const roViewport = new ResizeObserver(computeEdgeState);
      roViewport.observe(el);
      observers.push(roViewport);

      const track =
        trackRef.current || (el.firstElementChild as HTMLElement | null);
      if (track) {
        const roTrack = new ResizeObserver(computeEdgeState);
        roTrack.observe(track);
        observers.push(roTrack);
      }
    }

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", computeEdgeState);
      observers.forEach((o) => o.disconnect());
    };
  }, [observe, computeEdgeState]);

  const recompute = computeEdgeState;

  return {
    viewportRef,
    trackRef,
    atStart,
    atEnd,
    scrollBy,
    scrollPrev,
    scrollNext,
    reset,
    recompute,
  };
}
